import { TradutorAssistente } from '../../dominio/assistente.interface'

/**
 * QW6 — o selo fala a língua do usuário, não a do contrato.
 *
 * O que a pessoa via: "Resultado autorizado de acao_de_acompanhamento" —
 * identificador de ferramenta cru na cara de quem só queria saber quantos
 * atrasos houve —, e "1 resultados dentro do seu alcance", com a concordância
 * errada (R8).
 *
 * Duas regras, as duas puras:
 *
 * 1. Identificador de ferramenta NUNCA vaza. Conhecido vira nome humano;
 *    desconhecido some junto com a preposição que o amarrava, e o selo
 *    degrada para o genérico em vez de exibir `snake_case`.
 * 2. Número manda na concordância — inclusive nos `(s)` que o backend deixa
 *    para o leitor resolver.
 */

/**
 * Ferramentas publicadas hoje pelo servidor MCP. Duas ferramentas podem
 * compartilhar o mesmo nome humano: quem lê não precisa da distinção interna
 * entre "consultar estado" e "consultar estado para atualização".
 */
export const CHAVE_POR_FERRAMENTA: Record<string, string> = {
  listar_empregados: 'assistentePontotel.ferramentas.listarEmpregados',
  listar_feriados: 'assistentePontotel.ferramentas.listarFeriados',
  listar_capacidades_disponiveis: 'assistentePontotel.ferramentas.acoesDisponiveis',
  descobrir_acoes_do_assistente: 'assistentePontotel.ferramentas.acoesDisponiveis',
  consultar_sessao_atual: 'assistentePontotel.ferramentas.sessaoAtual',
  consultar_dias_da_folha: 'assistentePontotel.ferramentas.diasDaFolha',
  consultar_estado_de_feriado: 'assistentePontotel.ferramentas.situacaoDeFeriado',
  consultar_estado_para_atualizacao_de_feriado: 'assistentePontotel.ferramentas.situacaoDeFeriado',
  consultar_estado_verificacao_de_dias: 'assistentePontotel.ferramentas.situacaoDaVerificacaoDeDias',
  pre_visualizar_criacao_de_feriado: 'assistentePontotel.ferramentas.conferenciaDeCriacaoDeFeriado',
  pre_visualizar_atualizacao_de_feriado: 'assistentePontotel.ferramentas.conferenciaDeAlteracaoDeFeriado',
  pre_visualizar_remocao_de_feriado: 'assistentePontotel.ferramentas.conferenciaDeRemocaoDeFeriado',
  pre_visualizar_verificacao_de_dias: 'assistentePontotel.ferramentas.conferenciaDeVerificacaoDeDias',
  confirmar_criacao_de_feriado: 'assistentePontotel.ferramentas.criacaoDeFeriado',
  confirmar_atualizacao_de_feriado: 'assistentePontotel.ferramentas.alteracaoDeFeriado',
  confirmar_remocao_de_feriado: 'assistentePontotel.ferramentas.remocaoDeFeriado',
  confirmar_verificacao_de_dias: 'assistentePontotel.ferramentas.verificacaoDeDias',
  acao_de_acompanhamento: 'assistentePontotel.ferramentas.acompanhamentoDoDia'
}

/** Sobra digna quando nem o identificador nem a frase restam legíveis. */
export const CHAVE_SELO_GENERICO = 'assistentePontotel.badges.resultadoAutorizado'

/** `snake_case` — não ocorre em prosa, então não há falso positivo a temer. */
const PADRAO_DE_IDENTIFICADOR = /[a-z0-9]+(?:_[a-z0-9]+)+/g

/**
 * Marca interna para o identificador que não soubemos nomear: um caractere que
 * não existe em texto de interface. Um espaço, aqui, casaria com qualquer selo
 * de duas palavras.
 */
const SEM_NOME = String.fromCharCode(0)

/**
 * Substantivos em que a correção de número é segura. Sem esta lista, "1 mais"
 * viraria "1 mai": a régua é conservadora de propósito.
 */
const SUBSTANTIVOS_CONTAVEIS = new Set([
  'resultado', 'registro', 'empregado', 'pessoa', 'dia', 'hora', 'item',
  'ocorrência', 'ocorrencia', 'alteração', 'alteracao', 'pendência',
  'pendencia', 'ajuste', 'solicitação', 'solicitacao', 'feriado', 'atraso',
  'falta', 'autorizado', 'encontrado', 'considerado', 'aplicado'
])

/** Quantos vocábulos depois do número a quantidade ainda manda. */
const ALCANCE_DA_QUANTIDADE = 3

/**
 * Resolve `(s)` e conserta plural cravado quando a quantidade é 1.
 * Exportada porque a mesma régua serve ao rodapé da tabela do canvas.
 */
export function corrigirConcordancia (texto: string): string {
  let quantidade: number | null = null
  let distancia = 0

  return texto.replace(/\d+|[A-Za-zÀ-ÿ]+(?:\(s\))?/g, trecho => {
    if (/^\d+$/.test(trecho)) {
      quantidade = Number(trecho)
      distancia = 0
      return trecho
    }

    distancia += 1
    if (quantidade === null) return trecho
    if (distancia > ALCANCE_DA_QUANTIDADE) {
      quantidade = null
      return trecho
    }

    if (trecho.endsWith('(s)')) {
      const raiz = trecho.slice(0, -3)
      return quantidade === 1 ? raiz : `${raiz}s`
    }

    if (quantidade === 1 && /s$/.test(trecho)) {
      const singular = trecho.slice(0, -1)
      if (SUBSTANTIVOS_CONTAVEIS.has(singular.toLowerCase())) return singular
    }

    return trecho
  })
}

/**
 * Devolve o selo pronto para a bolha. Selo já humano ("Dados da sua empresa")
 * atravessa intacto; selo vazio continua vazio — não inventamos etiqueta.
 */
export function humanizarSelo (selo: string | null | undefined, t: TradutorAssistente): string {
  const bruto = (selo || '').trim()
  if (!bruto) return ''

  const nomeado = bruto.replace(PADRAO_DE_IDENTIFICADOR, identificador => {
    const chave = CHAVE_POR_FERRAMENTA[identificador]
    return chave ? t(chave) : SEM_NOME
  })

  const semSobra = nomeado.includes(SEM_NOME)
    ? nomeado
      .replace(new RegExp(`\\s*\\b(de|do|da|para|por)\\b\\s*${SEM_NOME}`, 'gi'), '')
      .replace(new RegExp(SEM_NOME, 'g'), '')
    : nomeado

  const limpo = corrigirConcordancia(semSobra).replace(/\s{2,}/g, ' ').trim()
  return limpo || t(CHAVE_SELO_GENERICO)
}
